import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

interface PatientResult {
	id: number;
	first_name: string;
	last_name: string;
	title: string;
	gender: string;
	address: string;
	suburb: string;
	postcode: string;
	work_number: string;
	mobile_number: string;
	email: string;
	home_number: string;
	date_of_birth: string;
	next_of_kin: string;
	medicare: string;
	type: "patient";
	events?: EventResult[];
}

interface EventResult {
	id: number;
	date_of_service: string;
	surgeon_id: number | null;
	surgeon?: {
		first_name: string;
		last_name: string;
	};
	hospital_id: number | null;
	patient: PatientResult | null;
	health_fund_id: number | null;
	billing_agent_id: number | null;
	account_type: string | null;
	status: string | null;
	paid_by: string | null;
	actual_oop: string | null;
	billing_tier: any;
	billing_notes: string | null;
	proposed_items: any;
	actual_items: any;
	items_validated: boolean;
	user_id: number | null;
	session_id: number | null;
	anaesthetist_id: number | null;
	assistant_id: number | null;
	procedure: string | null;
	type: "event";
}

interface SessionResult {
	id: number;
	date_of_service: string;
	start_time: string;
	modified_at: string;
	hospital: { name: string };
	surgeon: { first_name: string; last_name: string };
	events?: EventResult[];
	type: "session";
}

export type DBResult = PatientResult | EventResult | SessionResult;

interface SearchState {
	searching: boolean;
	hasSearched: boolean;
	results: DBResult[];
	selectedItem: DBResult | null;
	loadingDetail: boolean;
	cache: {
		patients: Record<number, PatientResult>;
		events: Record<number, EventResult>;
		sessions: Record<number, SessionResult>;
	};
}

const initialState: SearchState = {
	searching: false,
	hasSearched: false,
	results: [],
	selectedItem: null,
	loadingDetail: false, // Set loading when refetching details from BE when traversing search results.
	cache: {
		patients: {},
		events: {},
		sessions: {},
	},
};

export const searchSlice = createSlice({
	name: "search",
	initialState,
	reducers: {
		setSearching: (state, action: PayloadAction<boolean>) => {
			state.searching = action.payload;
		},
		setHasSearched: (state, action) => {
			state.hasSearched = action.payload;
		},
		setSearchResults: (state, action: PayloadAction<DBResult[]>) => {
			state.results = action.payload;
		},
		setSelectedItem: (state, action: PayloadAction<DBResult | null>) => {
			state.selectedItem = action.payload;
		},
		updateCache: (state, action: PayloadAction<DBResult>) => {
			const item = action.payload;
			if (item.type === "patient") {
				state.cache.patients[item.id] = item;
			} else if (item.type === "event") {
				state.cache.events[item.id] = item;
			} else if (item.type === "session") {
				state.cache.sessions[item.id] = item;
			}
		},
		setLoadingDetail: (state, action: PayloadAction<boolean>) => {
			state.loadingDetail = action.payload;
		},
	},
});

export const {
	setSearching,
	setHasSearched,
	setSearchResults,
	setSelectedItem,
	updateCache,
	setLoadingDetail,
} = searchSlice.actions;

export const selectResults = (state: RootState) => state.search.results;
export const selectSelectedItem = (state: RootState) =>
	state.search.selectedItem;

export const selectPatientCache = (state: RootState) =>
	state.search.cache.patients;
export const selectEventCache = (state: RootState) => state.search.cache.events;
export const selectSessionCache = (state: RootState) =>
	state.search.cache.sessions;
export const selectLoadingDetail = (state: RootState) =>
	state.search.loadingDetail;

export default searchSlice.reducer;
